import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import TabCreateView from 'src/components/Layout/TabCreateView';
import BasicDetails from 'src/components/CreateDuplicateVehicleTracking/BasicDetails';
import AdditionalDetails from 'src/components/CreateDuplicateVehicleTracking/AdditionalDetails';
import DeliveryReviewListTab from 'src/components/CreateDuplicateVehicleTracking/DeliveryReviewListTab';
import LoadReviewListTab from 'src/components/CreateDuplicateVehicleTracking/LoadReviewListTab';
import WorkflowReviewTab from 'src/components/CreateDuplicateVehicleTracking/WorkflowReviewTab';

function ReviewAndConfirm({
  formData,
  setFormData,
  handleOnChange,
  transporterSearchTerm,
  setTransporterSearchTerm,
  setVehicleSearchTerm,
  errorField,
  mobileNumberError,
  deliveryLineItem = [],
  loadLineItem = [],
  selectedWorkflowObj,
  selectedStageObj,
  selectedStep,
  setSelectedStep,
  disableSubmitButton,
  handleDuplicateTrackingSubmit,
}) {
  const authReducer = useSelector((state) => state.AuthReducer);
  const vehicleTrackingDataReducer = useSelector(
    (state) => state.VehicleTrackingReducer,
  );
  const VehicleTrackingOutwardReducer = useSelector(
    (state) => state.VehicleTrackingOutwardReducer,
  );
  const themeColor = authReducer?.userData?.tenant?.theme;
  const {
    workflows: availableWorkflows,
    workflowStagesForWorkflow,
    plantsData,
    unitOfMeasurements,
  } = vehicleTrackingDataReducer;
  const { transporterDropdownOptions, vehicleDropdownOptions } =
    VehicleTrackingOutwardReducer;

  const selectedWorkflowValue =
    selectedWorkflowObj?.id ?? selectedWorkflowObj ?? null;
  const selectedStationValue = selectedStageObj?.id ?? selectedStageObj ?? null;

  const choices = [
    { id: 1, name: 'Average' },
    { id: 2, name: 'Good' },
    { id: 3, name: 'Excellent' },
  ];

  const workflowName = useMemo(() => {
    if (selectedWorkflowObj?.name) return selectedWorkflowObj.name;
    return (
      availableWorkflows?.find(
        (workflow) => workflow.id === selectedWorkflowValue,
      )?.name || '-'
    );
  }, [availableWorkflows, selectedWorkflowObj, selectedWorkflowValue]);

  const stationName = useMemo(() => {
    if (selectedStageObj?.name) return selectedStageObj.name;
    return (
      workflowStagesForWorkflow?.find(
        (station) => station.id === selectedStationValue,
      )?.name || '-'
    );
  }, [selectedStageObj, selectedStationValue, workflowStagesForWorkflow]);

  const businessPartnerDataOption =
    transporterDropdownOptions?.map((transporterObj) => ({
      value: transporterObj.id,
      label: transporterObj.name,
    })) || [];

  const vehicleNumberDataOption =
    vehicleDropdownOptions?.map((vehicleObj) => ({
      value: vehicleObj.id,
      label: vehicleObj.vehicle_number,
    })) || [];

  const plantDataOption =
    plantsData?.map((plantObj) => ({
      value: plantObj.id,
      label: plantObj.name,
    })) || [];

  const workflowDataOption =
    availableWorkflows?.map((availableWorkflow) => ({
      value: availableWorkflow.id,
      label: availableWorkflow.name,
    })) || [];

  const unitOfMeasurementOptions =
    unitOfMeasurements?.map((unitObj) => ({
      label: unitObj.name,
      value: unitObj.id,
    })) || [];

  const transporterValue =
    businessPartnerDataOption.find(
      (option) => option.value === formData?.transporterId,
    ) || '';

  const vehicleValue =
    vehicleNumberDataOption.find(
      (option) => option.value === formData?.selectedVehicleOption?.value,
    ) ||
    formData?.selectedVehicleOption ||
    '';

  const plantLabel =
    plantDataOption.find((option) => option.value === formData?.plant)?.label ||
    '';
  const workflowLabel =
    workflowDataOption.find((option) => option.value === formData?.workflowId)
      ?.label || '';
  const unitOfMeasurementLabel =
    unitOfMeasurementOptions.find(
      (option) => option.value === formData?.unitOfMeasurementId,
    )?.label || '';
  const conditionLabel = (value) =>
    choices.find((choice) => String(choice.id) === String(value))?.name || '';

  const isTransporterInvalid =
    !formData?.transporterId && !formData?.transporterName;
  const isVehicleFormFieldEmpty =
    !formData?.selectedVehicleOption?.value && !formData?.vehicleNumber;

  function handlePreviousStep() {
    setSelectedStep(selectedStep - 1);
  }

  const reviewComponents = [
    {
      id: 1,
      title: 'Basic Details',
      component: (
        <BasicDetails
          formData={formData}
          isEditing={false}
          errorField={errorField}
          isTransporterInvalid={isTransporterInvalid}
          isVehicleFormFieldEmpty={isVehicleFormFieldEmpty}
          businessPartnerDataOption={businessPartnerDataOption}
          transporterValue={transporterValue}
          transporterSearchTerm={transporterSearchTerm}
          setTransporterSearchTerm={setTransporterSearchTerm}
          setFormData={setFormData}
          handleOnChange={handleOnChange}
          handleVehicleChange={undefined}
          setVehicleSearchTerm={setVehicleSearchTerm}
          fetchVehicleDropdownOptions={undefined}
          vehicleNumberDataOption={vehicleNumberDataOption}
          vehicleValue={vehicleValue}
          plantDataOption={plantDataOption}
          workflowDataOption={workflowDataOption}
          plantLabel={plantLabel}
          workflowLabel={workflowLabel}
        />
      ),
    },
    {
      id: 2,
      title: 'Additional Details',
      component: (
        <AdditionalDetails
          formData={formData}
          isEditing={false}
          handleOnChange={handleOnChange}
          mobileNumberError={mobileNumberError}
          themeColor={themeColor}
          unitOfMeasurementOptions={unitOfMeasurementOptions}
          setUnitOfMeasurementSearchTerm={undefined}
          unitOfMeasurementLabel={unitOfMeasurementLabel}
          choices={choices}
          conditionLabel={conditionLabel}
        />
      ),
    },
    {
      id: 3,
      title: 'Attached Deliverys',
      component: <DeliveryReviewListTab deliveryLineItem={deliveryLineItem} />,
    },
    {
      id: 4,
      title: 'Attached Loads',
      component: <LoadReviewListTab loadLineItem={loadLineItem} />,
    },
    {
      id: 5,
      title: 'Workflow',
      component: (
        <WorkflowReviewTab
          workflowName={workflowName}
          stationName={stationName}
        />
      ),
    },
  ];

  return (
    <>
      <div
        className={`div-flex-column rg-10 theme-vertical-scrollable-container-${themeColor}`}
      >
        <TabCreateView createLeadFormComponents={reviewComponents} />
      </div>

      <div className=" div-flex-row div-space-between div-align-center vertical-padding-10 horizontal-padding-10 border-top">
        <button
          type="button"
          className={`common-btn-outlined theme-border-color-${themeColor}`}
          onClick={handlePreviousStep}
          disabled={selectedStep === 1}
        >
          Back
        </button>
        <button
          type="button"
          className={`common-btn theme-background-${themeColor}`}
          onClick={handleDuplicateTrackingSubmit}
          disabled={disableSubmitButton}
        >
          Submit
        </button>
      </div>
    </>
  );
}

export default ReviewAndConfirm;
